import { createStore } from 'vuex'
import {
  login as apiLogin,
  dingtalkLogin as apiDingtalkLogin,
  logout as apiLogout
} from '../api/auth'
import { getRoles } from '../api/roles'
import { initSocket, disconnectSocket } from '../utils/socket'
import { getTeacherClassRelations, getTeacherClassRelationsByTeacher } from '../api/teacherClassRelations'
import { getTeacher } from '../api/teachers'
import evaluationProject from './modules/evaluationProject'
import evaluationType from './modules/evaluationType'
import classes from './modules/classes'
import classHonors from './modules/classHonors'
import notices from './modules/notices'
import teachers from './modules/teachers'
import students from './modules/students'
import devicesModule from './modules/devices'
import attendance from './modules/attendance'
import attendanceFaceEnrollment from './modules/attendanceFaceEnrollment'

/** API 角色名 -> 前端 role */
const ROLE_MAP = {
  admin: 'admin',
  subadmin: 'subadmin',
  teacher: 'teacher',
  dorm: 'dorm',
  dormitory_manager: 'dorm',
  dorm_manager: 'dorm',
  guard: 'guard',
  security_guard: 'guard',
  管理员: 'admin',
  子管理员: 'subadmin',
  超级管理员: 'admin',
  教务管理员: 'admin',
  德育管理员: 'admin',
  教师: 'teacher',
  老师: 'teacher',
  班主任: 'teacher',
  任课教师: 'teacher',
  宿管: 'dorm',
  宿管员: 'dorm',
  宿舍管理员: 'dorm',
  医务室: 'medical',
  校医: 'medical',
  医生: 'medical',
  学生会: 'studentCouncil',
  student_council: 'studentCouncil',
  student_union: 'studentCouncil',
  studentgroup: 'studentCouncil',
  student_group: 'studentCouncil',
  门卫: 'guard',
  门岗: 'guard',
  学生: 'student',
  家长: 'parent'
}

const ALLOWED_APP_ROLES = new Set(['admin', 'subadmin', 'teacher', 'dorm', 'medical', 'studentCouncil', 'guard'])
const PORTAL_ROLE_MAP = {
  admin: ['admin', 'subadmin'],
  teacher: ['teacher'],
  dorm: ['dorm'],
  medical: ['medical'],
  studentcouncil: ['studentCouncil'],
  guard: ['guard'],
  psychology: ['teacher', 'admin', 'subadmin']
}
const DEFAULT_ROLE_PRIORITY = ['admin', 'subadmin', 'studentCouncil', 'guard', 'teacher', 'dorm', 'medical']
const AUTH_PORTAL_STORAGE_KEY = 'authPortal'
const LOGIN_PORTAL_STORAGE_KEY = 'loginPortal'

function normalizePortal(portal) {
  const text = String(portal || '').trim().toLowerCase()
  return PORTAL_ROLE_MAP[text] ? text : ''
}

function resolvePortalFromRole(role = '') {
  if (role === 'admin' || role === 'subadmin') return 'admin'
  if (role === 'teacher') return 'teacher'
  if (role === 'dorm') return 'dorm'
  if (role === 'medical') return 'medical'
  if (role === 'studentCouncil') return 'studentcouncil'
  if (role === 'guard') return 'guard'
  return ''
}

function persistPortalSelection(portal = '') {
  const normalizedPortal = normalizePortal(portal)
  if (!normalizedPortal) return
  localStorage.setItem(AUTH_PORTAL_STORAGE_KEY, normalizedPortal)
  localStorage.setItem(LOGIN_PORTAL_STORAGE_KEY, normalizedPortal)
}

function normalizeRole(rawRole) {
  if (!rawRole) return ''
  if (typeof rawRole === 'object') {
    const nestedCandidates = [
      rawRole.role,
      rawRole.role_name,
      rawRole.roleName,
      rawRole.name,
      rawRole.code,
      rawRole.value,
      rawRole.key,
      rawRole.authority
    ]
    for (const candidate of nestedCandidates) {
      const normalized = normalizeRole(candidate)
      if (normalized) return normalized
    }
    return ''
  }

  const text = String(rawRole).trim()
  if (!text) return ''

  if (ROLE_MAP[text]) return ROLE_MAP[text]

  const lower = text.toLowerCase()
  if (ROLE_MAP[lower]) return ROLE_MAP[lower]
  if (lower.includes('medical')) return 'medical'
  if (lower.includes('guard') || text.includes('门卫') || text.includes('门岗')) return 'guard'
  if (
    lower.includes('student_council')
    || lower.includes('student-council')
    || lower.includes('student_union')
    || lower.includes('student-union')
    || lower.includes('studentgroup')
    || lower.includes('student_group')
    || text.includes('学生会')
  ) return 'studentCouncil'
  if (text.includes('子管理员') || /(^|[_-])subadmin([_-]|$)/.test(lower)) return 'subadmin'
  if (lower.includes('dorm') || (text.includes('宿') && text.includes('管'))) return 'dorm'
  if (lower.includes('teacher') || text.includes('教师') || text.includes('老师') || text.includes('班主任') || text.includes('任课')) return 'teacher'
  if (lower.includes('admin') || text.includes('管理员') || text.includes('教务') || text.includes('德育')) return 'admin'
  if (lower.includes('student') || text.includes('学生')) return 'student'
  if (lower.includes('parent') || text.includes('家长')) return 'parent'
  return ''
}

function toUniqueAllowedRoles(list = []) {
  const unique = []
  list.forEach((role) => {
    if (!isAllowedRole(role) || unique.includes(role)) return
    unique.push(role)
  })
  return unique
}

function collectRoleCandidates(source) {
  if (source == null) return []
  if (Array.isArray(source)) return source.flatMap(item => collectRoleCandidates(item))
  if (typeof source !== 'object') return [source]

  return [
    source.role,
    source.role_name,
    source.roleName,
    source.role_code,
    source.roleCode,
    source.authority,
    source.roles,
    source.role_info,
    source.roleInfo,
    source.permissions,
    source.permission
  ].flatMap(item => collectRoleCandidates(item))
}

function getRolesFromUserInfo(userInfo = {}) {
  return toUniqueAllowedRoles(
    collectRoleCandidates(userInfo)
      .map(item => normalizeRole(item))
      .filter(Boolean)
  )
}

function getRoleFromUserInfo(userInfo = {}) {
  return getRolesFromUserInfo(userInfo)[0] || ''
}

function getMedicalDoctorName(userInfo = {}) {
  return String(
    userInfo?.school_doctor_info?.doctor_name
    || userInfo?.schoolDoctorInfo?.doctor_name
    || userInfo?.schoolDoctorInfo?.doctorName
    || userInfo?.doctor_name
    || ''
  ).trim()
}

function getPsychologyCounselorName(userInfo = {}) {
  return String(
    userInfo?.psychology_counselor_info?.counselor_name
    || userInfo?.psychologyCounselorInfo?.counselor_name
    || userInfo?.psychologyCounselorInfo?.counselorName
    || userInfo?.counselor_name
    || ''
  ).trim()
}

function getGuardName(userInfo = {}) {
  return String(
    userInfo?.guard_info?.guard_name
    || userInfo?.guardInfo?.guard_name
    || userInfo?.guardInfo?.guardName
    || userInfo?.guard_name
    || ''
  ).trim()
}

function getUserDisplayName(userInfo = {}, role = '') {
  const resolvedRole = role || getRoleFromUserInfo(userInfo)
  const medicalDoctorName = getMedicalDoctorName(userInfo)
  const psychologyCounselorName = getPsychologyCounselorName(userInfo)
  const guardName = getGuardName(userInfo)
  if (resolvedRole === 'medical' && medicalDoctorName) return medicalDoctorName
  if (psychologyCounselorName) return psychologyCounselorName
  if (resolvedRole === 'guard' && guardName) return guardName
  return String(
    userInfo?.real_name
    || userInfo?.teacher_name
    || userInfo?.nickname
    || userInfo?.name
    || medicalDoctorName
    || psychologyCounselorName
    || guardName
    || userInfo?.username
    || '用户'
  ).trim()
}

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length < 2) return null
  const encoded = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  const padded = encoded + '='.repeat((4 - (encoded.length % 4)) % 4)
  try {
    const json = atob(padded)
    return JSON.parse(json)
  } catch (_) {
    return null
  }
}

function getRolesFromToken(token) {
  const payload = decodeJwtPayload(token)
  if (!payload) return []
  return toUniqueAllowedRoles(
    [
      ...collectRoleCandidates(payload),
      ...collectRoleCandidates(payload?.user_info || {}),
      ...collectRoleCandidates(payload?.userInfo || {})
    ]
      .map(item => normalizeRole(item))
      .filter(Boolean)
  )
}

function getRoleFromToken(token) {
  return getRolesFromToken(token)[0] || ''
}

function isAllowedRole(role) {
  return ALLOWED_APP_ROLES.has(role)
}

function pickRoleByPortal(roles = [], portal = '') {
  const normalizedPortal = normalizePortal(portal)
  const preferredRoles = normalizedPortal ? PORTAL_ROLE_MAP[normalizedPortal] : DEFAULT_ROLE_PRIORITY
  return preferredRoles.find(role => roles.includes(role)) || ''
}

function getPortalLabel(portal = '') {
  switch (normalizePortal(portal)) {
    case 'admin':
      return '管理端'
    case 'teacher':
      return '教师端'
    case 'dorm':
      return '宿管端'
    case 'medical':
      return '医务室'
    case 'studentcouncil':
      return '学生会'
    case 'guard':
      return '门卫端'
    case 'psychology':
      return '心理室'
    default:
      return '当前端'
  }
}

function buildPortalPermissionError(portal = '') {
  return `当前账号没有${getPortalLabel(portal)}权限，请切换后重新登录`
}

function looksLikeUserInfo(value) {
  return !!(value && typeof value === 'object' && (
    value.user_id != null
    || value.username != null
    || value.role != null
    || value.roles != null
  ))
}

function getAuthResponseMessage(response, fallback = '') {
  return String(
    response?.error
    || response?.message
    || response?.msg
    || response?.data?.error
    || response?.data?.message
    || response?.data?.msg
    || fallback
  ).trim()
}

function normalizeAuthResponse(response = {}) {
  const data = response?.data && typeof response.data === 'object' ? response.data : {}
  const token = String(
    response?.token
    || response?.access_token
    || data?.token
    || data?.access_token
    || ''
  ).trim()
  const userInfo = response?.user_info
    || response?.userInfo
    || data?.user_info
    || data?.userInfo
    || (looksLikeUserInfo(data) ? data : {})

  const explicitSuccess = [response?.success, data?.success].find(value => typeof value === 'boolean')
  const successByCode = [response?.code, response?.status, data?.code, data?.status]
    .some(value => String(value ?? '').trim() === '200' || String(value ?? '').trim() === '0')
  const success = typeof explicitSuccess === 'boolean' ? explicitSuccess : (successByCode || !!token)

  return {
    success,
    token,
    userInfo,
    message: getAuthResponseMessage(response)
  }
}

function resolveAuthRole({ token = '', userInfo = {}, targetPortal = '', savedRole = '' } = {}) {
  const tokenRoles = getRolesFromToken(token)
  const infoRoles = getRolesFromUserInfo(userInfo)
  const availableRoles = toUniqueAllowedRoles([...tokenRoles, ...infoRoles])
  if (!availableRoles.length) {
    throw new Error('您的账号暂不支持登录该端')
  }

  const portalRole = pickRoleByPortal(availableRoles, targetPortal)
  if (normalizePortal(targetPortal) && !portalRole) {
    throw new Error(buildPortalPermissionError(targetPortal))
  }

  const persistedRole = isAllowedRole(savedRole) && availableRoles.includes(savedRole) ? savedRole : ''
  const role = portalRole || persistedRole || pickRoleByPortal(availableRoles)
  if (!role) {
    throw new Error('您的账号暂不支持登录该端')
  }

  return { role, availableRoles }
}

const TEACHER_ACTIVE_CLASS_STORAGE_KEY = 'teacherActiveClassId'

function buildTeacherClassEntry(cls = {}, relation = {}) {
  const relationType = Number(relation.relation_type ?? relation.relationType ?? 0)
  return {
    id: cls.id ?? cls.class_id ?? relation.class_id ?? relation.id ?? '',
    class_id: cls.class_id ?? cls.id ?? relation.class_id ?? relation.id ?? '',
    name: cls.name ?? cls.class_name ?? relation.class_name ?? relation.name ?? '',
    grade: cls.grade ?? relation.grade ?? '',
    boardId: cls.boardId ?? cls.device_id ?? cls.board_id ?? relation.board_id ?? relation.device_id ?? '',
    machineCode: cls.machineCode ?? cls.machine_code ?? relation.machine_code ?? '',
    relationId: relation.relation_id ?? relation.id ?? null,
    relationType,
    isHeadTeacher: relationType === 1
  }
}

function cloneTeacherClassEntry(entry = {}) {
  return {
    id: entry.id ?? entry.class_id ?? '',
    class_id: entry.class_id ?? entry.id ?? '',
    name: entry.name ?? entry.class_name ?? '',
    grade: entry.grade ?? '',
    boardId: entry.boardId ?? entry.device_id ?? entry.board_id ?? '',
    machineCode: entry.machineCode ?? entry.machine_code ?? '',
    relationId: entry.relationId ?? entry.relation_id ?? null,
    relationType: Number(entry.relationType ?? entry.relation_type ?? 0),
    isHeadTeacher: !!(entry.isHeadTeacher ?? Number(entry.relationType ?? entry.relation_type ?? 0) === 1)
  }
}

function extractListFromResponse(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.records)) return payload.data.records
  if (Array.isArray(payload?.data?.list)) return payload.data.list
  if (Array.isArray(payload?.records)) return payload.records
  if (Array.isArray(payload?.list)) return payload.list
  return []
}

function normalizeText(value) {
  return String(value ?? '').trim()
}

function applyActiveTeacherClass(state, entry) {
  const next = cloneTeacherClassEntry(entry)
  state.teacherClass = {
    id: next.id,
    name: next.name,
    grade: next.grade,
    boardId: next.boardId,
    machineCode: next.machineCode,
    class_id: next.class_id,
    relationId: next.relationId,
    relationType: next.relationType,
    isHeadTeacher: next.isHeadTeacher
  }
  state.teacherIsHeadTeacher = !!next.isHeadTeacher
}

function clearAuthStorage() {
  localStorage.removeItem('token')
  localStorage.removeItem('userInfo')
  localStorage.removeItem('authRole')
  localStorage.removeItem(AUTH_PORTAL_STORAGE_KEY)
  localStorage.removeItem(TEACHER_ACTIVE_CLASS_STORAGE_KEY)
}

let authInitPromise = null

export default createStore({
  state: {
    // 登录态恢复是否已经完成（用于避免启动期登录页闪烁）
    authReady: false,
    // WebSocket 连接状态
    socketConnected: false,
    // 实时通知列表（未已读前不关闭，持久化到 localStorage）
    notifications: [],
    // 是否已登录
    isLoggedIn: false,
    // JWT token
    token: '',
    // API 返回的完整用户信息
    userInfo: null,
    // 当前用户角色: 'admin' | 'subadmin' | 'teacher' | 'dorm' | 'medical' | 'studentCouncil' | 'guard'
    role: '',
    // 当前账号可用的前端角色集合
    authRoles: [],
    // 所有权限/角色列表（来自 /api/roles）
    rolesList: [],
    // 后端返回的动态导航结构（融合菜单）
    serverNavigation: null,
    // 当前用户信息（兼容旧逻辑，从 userInfo 派生）
    currentUser: {
      id: 1,
      name: '李主任',
      avatar: '李',
      department: '教务处'
    },
    // 教师绑定的班级（教师端用，当前选中的班级）
    teacherClass: {
      id: 'class-1-1',
      name: '高一(1)班',
      grade: '高一',
      boardId: 'BP-2026-001',
      machineCode: 'MC-A3F7B2E1'
    },
    // 教师所教的全部班级（一个老师可教多个班）
    teacherClasses: [
      { id: 'class-1-1', class_id: 'class-1-1', name: '高一(1)班', relationType: 1, isHeadTeacher: true },
      { id: 'class-1-2', class_id: 'class-1-2', name: '高一(2)班', relationType: 0, isHeadTeacher: false }
    ],
    // 当前登录教师信息（来自 getTeacher(user_id)，用于显示姓名等）
    currentTeacher: null,
    // 教师端：当前班级下是否为班主任（relation_type=1），任课教师(0)则大部分增删改操作隐藏
    teacherIsHeadTeacher: true,
    // 所有班牌设备（15个，用于演示翻页）
    boards: [
      { id: 'BP-2026-001', machineCode: 'MC-A3F7B2E1', classId: 'class-1-1', className: '高一(1)班', status: 'online', lastSync: '2026-02-09 19:30', template: 'classic' },
      { id: 'BP-2026-002', machineCode: 'MC-B4G8C3F2', classId: 'class-1-2', className: '高一(2)班', status: 'online', lastSync: '2026-02-09 19:28', template: 'minimal' },
      { id: 'BP-2026-003', machineCode: 'MC-C5H9D4G3', classId: 'class-2-1', className: '高二(1)班', status: 'offline', lastSync: '2026-02-09 17:15', template: 'card' },
      { id: 'BP-2026-004', machineCode: 'MC-D6I0E5H4', classId: 'class-2-2', className: '高二(2)班', status: 'online', lastSync: '2026-02-09 19:25', template: 'diy' },
      { id: 'BP-2026-005', machineCode: '', classId: '', className: '', status: 'unbound', lastSync: '', template: 'classic' },
      { id: 'BP-2026-006', machineCode: 'MC-E7J1F6I5', classId: 'class-1-1', className: '高一(1)班', status: 'online', lastSync: '2026-02-09 19:22', template: 'classic' },
      { id: 'BP-2026-007', machineCode: 'MC-F8K2G7J6', classId: 'class-1-2', className: '高一(2)班', status: 'online', lastSync: '2026-02-09 19:18', template: 'minimal' },
      { id: 'BP-2026-008', machineCode: 'MC-G9L3H8K7', classId: '', className: '', status: 'unbound', lastSync: '', template: 'classic' },
      { id: 'BP-2026-009', machineCode: 'MC-H0M4I9L8', classId: 'class-2-1', className: '高二(1)班', status: 'offline', lastSync: '2026-02-09 16:45', template: 'card' },
      { id: 'BP-2026-010', machineCode: 'MC-I1N5J0M9', classId: 'class-2-2', className: '高二(2)班', status: 'online', lastSync: '2026-02-09 19:20', template: 'diy' },
      { id: 'BP-2026-011', machineCode: 'MC-J2O6K1N0', classId: '', className: '', status: 'unbound', lastSync: '', template: 'classic' },
      { id: 'BP-2026-012', machineCode: 'MC-K3P7L2O1', classId: 'class-1-1', className: '高一(1)班', status: 'online', lastSync: '2026-02-09 19:15', template: 'minimal' },
      { id: 'BP-2026-013', machineCode: 'MC-L4Q8M3P2', classId: 'class-1-2', className: '高一(2)班', status: 'offline', lastSync: '2026-02-09 18:30', template: 'card' },
      { id: 'BP-2026-014', machineCode: 'MC-M5R9N4Q3', classId: 'class-2-1', className: '高二(1)班', status: 'online', lastSync: '2026-02-09 19:28', template: 'diy' },
      { id: 'BP-2026-015', machineCode: '', classId: '', className: '', status: 'unbound', lastSync: '', template: 'classic' }
    ],
    // 所有班级（15个，高一5、高二5、高三5）
    classes: [
      { id: 'class-1-1', name: '高一(1)班', grade: '高一', teacher: '张老师', studentCount: 45, boardId: 'BP-2026-001' },
      { id: 'class-1-2', name: '高一(2)班', grade: '高一', teacher: '王老师', studentCount: 43, boardId: 'BP-2026-002' },
      { id: 'class-1-3', name: '高一(3)班', grade: '高一', teacher: '李老师', studentCount: 44, boardId: '' },
      { id: 'class-1-4', name: '高一(4)班', grade: '高一', teacher: '刘老师', studentCount: 42, boardId: '' },
      { id: 'class-1-5', name: '高一(5)班', grade: '高一', teacher: '周老师', studentCount: 46, boardId: '' },
      { id: 'class-2-1', name: '高二(1)班', grade: '高二', teacher: '赵老师', studentCount: 44, boardId: 'BP-2026-003' },
      { id: 'class-2-2', name: '高二(2)班', grade: '高二', teacher: '陈老师', studentCount: 42, boardId: 'BP-2026-004' },
      { id: 'class-2-3', name: '高二(3)班', grade: '高二', teacher: '吴老师', studentCount: 45, boardId: '' },
      { id: 'class-2-4', name: '高二(4)班', grade: '高二', teacher: '郑老师', studentCount: 43, boardId: '' },
      { id: 'class-2-5', name: '高二(5)班', grade: '高二', teacher: '孙老师', studentCount: 41, boardId: '' },
      { id: 'class-3-1', name: '高三(1)班', grade: '高三', teacher: '钱老师', studentCount: 48, boardId: '' },
      { id: 'class-3-2', name: '高三(2)班', grade: '高三', teacher: '林老师', studentCount: 46, boardId: '' },
      { id: 'class-3-3', name: '高三(3)班', grade: '高三', teacher: '何老师', studentCount: 44, boardId: '' },
      { id: 'class-3-4', name: '高三(4)班', grade: '高三', teacher: '徐老师', studentCount: 45, boardId: '' },
      { id: 'class-3-5', name: '高三(5)班', grade: '高三', teacher: '朱老师', studentCount: 43, boardId: '' }
    ],
    // 按班级组织的学生数据（每班 15 个假数据）
    studentsByClass: {
      'class-1-1': [
        { id: 's001', name: '张小明', gender: '男', studentNo: '20260101', phone: '138****1234', status: 'normal' },
        { id: 's002', name: '李小红', gender: '女', studentNo: '20260102', phone: '139****5678', status: 'normal' },
        { id: 's003', name: '王小华', gender: '男', studentNo: '20260103', phone: '137****9012', status: 'leave' },
        { id: 's004', name: '赵小丽', gender: '女', studentNo: '20260104', phone: '136****3456', status: 'normal' },
        { id: 's005', name: '陈小军', gender: '男', studentNo: '20260105', phone: '135****7890', status: 'normal' },
        { id: 's006', name: '钱小伟', gender: '男', studentNo: '20260106', phone: '134****2345', status: 'normal' },
        { id: 's007', name: '孙小芳', gender: '女', studentNo: '20260107', phone: '133****6789', status: 'normal' },
        { id: 's008', name: '周小杰', gender: '男', studentNo: '20260108', phone: '132****0123', status: 'leave' },
        { id: 's009', name: '吴小琳', gender: '女', studentNo: '20260109', phone: '131****4567', status: 'normal' },
        { id: 's010', name: '郑小龙', gender: '男', studentNo: '20260110', phone: '130****8901', status: 'normal' },
        { id: 's011', name: '冯小亮', gender: '男', studentNo: '20260111', phone: '139****1234', status: 'normal' },
        { id: 's012', name: '许小梅', gender: '女', studentNo: '20260112', phone: '138****5678', status: 'normal' },
        { id: 's013', name: '韩小冬', gender: '男', studentNo: '20260113', phone: '137****9012', status: 'normal' },
        { id: 's014', name: '曹小芳', gender: '女', studentNo: '20260114', phone: '136****3456', status: 'normal' },
        { id: 's015', name: '曾小华', gender: '男', studentNo: '20260115', phone: '135****7890', status: 'leave' }
      ],
      'class-1-2': [
        { id: 's101', name: '刘小强', gender: '男', studentNo: '20260201', phone: '138****2345', status: 'normal' },
        { id: 's102', name: '孙小伟', gender: '男', studentNo: '20260202', phone: '139****6789', status: 'normal' },
        { id: 's103', name: '周小杰', gender: '女', studentNo: '20260203', phone: '137****0123', status: 'normal' },
        { id: 's104', name: '吴小峰', gender: '男', studentNo: '20260204', phone: '136****4567', status: 'normal' },
        { id: 's105', name: '郑小龙', gender: '男', studentNo: '20260205', phone: '135****8901', status: 'normal' },
        { id: 's106', name: '马小云', gender: '女', studentNo: '20260206', phone: '134****3456', status: 'normal' },
        { id: 's107', name: '黄小金', gender: '男', studentNo: '20260207', phone: '133****7890', status: 'leave' },
        { id: 's108', name: '彭小丽', gender: '女', studentNo: '20260208', phone: '132****0123', status: 'normal' },
        { id: 's109', name: '吕小强', gender: '男', studentNo: '20260209', phone: '131****4567', status: 'normal' },
        { id: 's110', name: '丁小伟', gender: '男', studentNo: '20260210', phone: '130****8901', status: 'normal' },
        { id: 's111', name: '任小杰', gender: '男', studentNo: '20260211', phone: '139****1234', status: 'normal' },
        { id: 's112', name: '沈小兰', gender: '女', studentNo: '20260212', phone: '138****5678', status: 'normal' },
        { id: 's113', name: '姚小刚', gender: '男', studentNo: '20260213', phone: '137****9012', status: 'leave' },
        { id: 's114', name: '谭小玲', gender: '女', studentNo: '20260214', phone: '136****3456', status: 'normal' },
        { id: 's115', name: '陆小峰', gender: '男', studentNo: '20260215', phone: '135****7890', status: 'normal' }
      ],
      'class-1-3': [
        { id: 's201', name: '冯小亮', gender: '男', studentNo: '20260131', phone: '138****1001', status: 'normal' },
        { id: 's202', name: '许小梅', gender: '女', studentNo: '20260132', phone: '139****1002', status: 'normal' },
        { id: 's203', name: '韩小冬', gender: '男', studentNo: '20260133', phone: '137****1003', status: 'normal' },
        { id: 's204', name: '曹小芳', gender: '女', studentNo: '20260134', phone: '136****1004', status: 'leave' },
        { id: 's205', name: '曾小华', gender: '男', studentNo: '20260135', phone: '135****1005', status: 'normal' },
        { id: 's206', name: '彭小丽', gender: '女', studentNo: '20260136', phone: '134****1006', status: 'normal' },
        { id: 's207', name: '吕小强', gender: '男', studentNo: '20260137', phone: '138****1007', status: 'normal' },
        { id: 's208', name: '丁小伟', gender: '男', studentNo: '20260138', phone: '139****1008', status: 'normal' },
        { id: 's209', name: '任小杰', gender: '男', studentNo: '20260139', phone: '137****1009', status: 'normal' },
        { id: 's210', name: '沈小兰', gender: '女', studentNo: '20260140', phone: '136****1010', status: 'normal' },
        { id: 's211', name: '姚小刚', gender: '男', studentNo: '20260141', phone: '135****1011', status: 'leave' },
        { id: 's212', name: '谭小玲', gender: '女', studentNo: '20260142', phone: '134****1012', status: 'normal' },
        { id: 's213', name: '陆小峰', gender: '男', studentNo: '20260143', phone: '138****1013', status: 'normal' },
        { id: 's214', name: '范小燕', gender: '女', studentNo: '20260144', phone: '139****1014', status: 'normal' },
        { id: 's215', name: '石小鹏', gender: '男', studentNo: '20260145', phone: '137****1015', status: 'normal' }
      ],
      'class-1-4': [
        { id: 's301', name: '韩小冬', gender: '男', studentNo: '20260151', phone: '137****1003', status: 'normal' },
        { id: 's302', name: '曹小芳', gender: '女', studentNo: '20260152', phone: '136****1004', status: 'leave' },
        { id: 's303', name: '曾小华', gender: '男', studentNo: '20260153', phone: '135****1005', status: 'normal' },
        { id: 's304', name: '彭小丽', gender: '女', studentNo: '20260154', phone: '134****1006', status: 'normal' },
        { id: 's305', name: '吕小强', gender: '男', studentNo: '20260155', phone: '138****1007', status: 'normal' },
        { id: 's306', name: '丁小伟', gender: '男', studentNo: '20260156', phone: '139****1008', status: 'normal' },
        { id: 's307', name: '任小杰', gender: '男', studentNo: '20260157', phone: '137****1009', status: 'normal' },
        { id: 's308', name: '沈小兰', gender: '女', studentNo: '20260158', phone: '136****1010', status: 'normal' },
        { id: 's309', name: '姚小刚', gender: '男', studentNo: '20260159', phone: '135****1011', status: 'leave' },
        { id: 's310', name: '谭小玲', gender: '女', studentNo: '20260160', phone: '134****1012', status: 'normal' },
        { id: 's311', name: '陆小峰', gender: '男', studentNo: '20260161', phone: '138****1013', status: 'normal' },
        { id: 's312', name: '范小燕', gender: '女', studentNo: '20260162', phone: '139****1014', status: 'normal' },
        { id: 's313', name: '石小鹏', gender: '男', studentNo: '20260163', phone: '137****1015', status: 'normal' },
        { id: 's314', name: '廖小慧', gender: '女', studentNo: '20260164', phone: '136****1016', status: 'normal' },
        { id: 's315', name: '邹小涛', gender: '男', studentNo: '20260165', phone: '135****1017', status: 'normal' }
      ],
      'class-1-5': [
        { id: 's401', name: '曾小华', gender: '男', studentNo: '20260171', phone: '135****1005', status: 'normal' },
        { id: 's402', name: '彭小丽', gender: '女', studentNo: '20260172', phone: '134****1006', status: 'normal' },
        { id: 's403', name: '吕小强', gender: '男', studentNo: '20260173', phone: '138****1007', status: 'normal' },
        { id: 's404', name: '丁小伟', gender: '男', studentNo: '20260174', phone: '139****1008', status: 'normal' },
        { id: 's405', name: '任小杰', gender: '男', studentNo: '20260175', phone: '137****1009', status: 'normal' },
        { id: 's406', name: '沈小兰', gender: '女', studentNo: '20260176', phone: '136****1010', status: 'normal' },
        { id: 's407', name: '姚小刚', gender: '男', studentNo: '20260177', phone: '135****1011', status: 'leave' },
        { id: 's408', name: '谭小玲', gender: '女', studentNo: '20260178', phone: '134****1012', status: 'normal' },
        { id: 's409', name: '陆小峰', gender: '男', studentNo: '20260179', phone: '138****1013', status: 'normal' },
        { id: 's410', name: '范小燕', gender: '女', studentNo: '20260180', phone: '139****1014', status: 'normal' },
        { id: 's411', name: '石小鹏', gender: '男', studentNo: '20260181', phone: '137****1015', status: 'normal' },
        { id: 's412', name: '廖小慧', gender: '女', studentNo: '20260182', phone: '136****1016', status: 'normal' },
        { id: 's413', name: '邹小涛', gender: '男', studentNo: '20260183', phone: '135****1017', status: 'normal' },
        { id: 's414', name: '熊小敏', gender: '女', studentNo: '20260184', phone: '134****1018', status: 'leave' },
        { id: 's415', name: '金小波', gender: '男', studentNo: '20260185', phone: '138****1019', status: 'normal' }
      ],
      'class-2-1': [
        { id: 's501', name: '吴小峰', gender: '男', studentNo: '20260301', phone: '136****4567', status: 'normal' },
        { id: 's502', name: '郑小龙', gender: '男', studentNo: '20260302', phone: '135****8901', status: 'normal' },
        { id: 's503', name: '马小云', gender: '女', studentNo: '20260303', phone: '134****3456', status: 'normal' },
        { id: 's504', name: '黄小金', gender: '男', studentNo: '20260304', phone: '133****7890', status: 'normal' },
        { id: 's505', name: '冯小亮', gender: '男', studentNo: '20260305', phone: '132****0123', status: 'leave' },
        { id: 's506', name: '许小梅', gender: '女', studentNo: '20260306', phone: '131****4567', status: 'normal' },
        { id: 's507', name: '韩小冬', gender: '男', studentNo: '20260307', phone: '130****8901', status: 'normal' },
        { id: 's508', name: '曹小芳', gender: '女', studentNo: '20260308', phone: '139****1234', status: 'normal' },
        { id: 's509', name: '曾小华', gender: '男', studentNo: '20260309', phone: '138****5678', status: 'normal' },
        { id: 's510', name: '彭小丽', gender: '女', studentNo: '20260310', phone: '137****9012', status: 'normal' },
        { id: 's511', name: '吕小强', gender: '男', studentNo: '20260311', phone: '136****3456', status: 'normal' },
        { id: 's512', name: '丁小伟', gender: '男', studentNo: '20260312', phone: '135****7890', status: 'leave' },
        { id: 's513', name: '任小杰', gender: '男', studentNo: '20260313', phone: '134****0123', status: 'normal' },
        { id: 's514', name: '沈小兰', gender: '女', studentNo: '20260314', phone: '133****4567', status: 'normal' },
        { id: 's515', name: '姚小刚', gender: '男', studentNo: '20260315', phone: '132****8901', status: 'normal' }
      ],
      'class-2-2': [
        { id: 's601', name: '马小云', gender: '女', studentNo: '20260401', phone: '138****3456', status: 'normal' },
        { id: 's602', name: '黄小金', gender: '男', studentNo: '20260402', phone: '139****7890', status: 'normal' },
        { id: 's603', name: '冯小亮', gender: '男', studentNo: '20260403', phone: '137****0123', status: 'normal' },
        { id: 's604', name: '许小梅', gender: '女', studentNo: '20260404', phone: '136****4567', status: 'normal' },
        { id: 's605', name: '韩小冬', gender: '男', studentNo: '20260405', phone: '135****8901', status: 'leave' },
        { id: 's606', name: '曹小芳', gender: '女', studentNo: '20260406', phone: '134****1234', status: 'normal' },
        { id: 's607', name: '曾小华', gender: '男', studentNo: '20260407', phone: '133****5678', status: 'normal' },
        { id: 's608', name: '彭小丽', gender: '女', studentNo: '20260408', phone: '132****9012', status: 'normal' },
        { id: 's609', name: '吕小强', gender: '男', studentNo: '20260409', phone: '131****3456', status: 'normal' },
        { id: 's610', name: '丁小伟', gender: '男', studentNo: '20260410', phone: '130****7890', status: 'normal' },
        { id: 's611', name: '任小杰', gender: '男', studentNo: '20260411', phone: '139****0123', status: 'leave' },
        { id: 's612', name: '沈小兰', gender: '女', studentNo: '20260412', phone: '138****4567', status: 'normal' },
        { id: 's613', name: '姚小刚', gender: '男', studentNo: '20260413', phone: '137****8901', status: 'normal' },
        { id: 's614', name: '谭小玲', gender: '女', studentNo: '20260414', phone: '136****1234', status: 'normal' },
        { id: 's615', name: '陆小峰', gender: '男', studentNo: '20260415', phone: '135****5678', status: 'normal' }
      ],
      'class-2-3': [
        { id: 's701', name: '吕小强', gender: '男', studentNo: '20260201', phone: '138****1007', status: 'normal' },
        { id: 's702', name: '丁小伟', gender: '男', studentNo: '20260202', phone: '139****1008', status: 'normal' },
        { id: 's703', name: '任小杰', gender: '男', studentNo: '20260203', phone: '137****1009', status: 'normal' },
        { id: 's704', name: '沈小兰', gender: '女', studentNo: '20260204', phone: '136****1010', status: 'normal' },
        { id: 's705', name: '姚小刚', gender: '男', studentNo: '20260205', phone: '135****1011', status: 'leave' },
        { id: 's706', name: '谭小玲', gender: '女', studentNo: '20260206', phone: '134****1012', status: 'normal' },
        { id: 's707', name: '陆小峰', gender: '男', studentNo: '20260207', phone: '138****1013', status: 'normal' },
        { id: 's708', name: '范小燕', gender: '女', studentNo: '20260208', phone: '139****1014', status: 'normal' },
        { id: 's709', name: '石小鹏', gender: '男', studentNo: '20260209', phone: '137****1015', status: 'normal' },
        { id: 's710', name: '廖小慧', gender: '女', studentNo: '20260210', phone: '136****1016', status: 'normal' },
        { id: 's711', name: '邹小涛', gender: '男', studentNo: '20260211', phone: '135****1017', status: 'normal' },
        { id: 's712', name: '熊小敏', gender: '女', studentNo: '20260212', phone: '134****1018', status: 'leave' },
        { id: 's713', name: '金小波', gender: '男', studentNo: '20260213', phone: '138****1019', status: 'normal' },
        { id: 's714', name: '魏小静', gender: '女', studentNo: '20260214', phone: '139****1020', status: 'normal' },
        { id: 's715', name: '陶小勇', gender: '男', studentNo: '20260215', phone: '137****1021', status: 'normal' }
      ],
      'class-2-4': [
        { id: 's801', name: '任小杰', gender: '男', studentNo: '20260203', phone: '137****1009', status: 'normal' },
        { id: 's802', name: '沈小兰', gender: '女', studentNo: '20260204', phone: '136****1010', status: 'normal' },
        { id: 's803', name: '姚小刚', gender: '男', studentNo: '20260205', phone: '135****1011', status: 'leave' },
        { id: 's804', name: '谭小玲', gender: '女', studentNo: '20260206', phone: '134****1012', status: 'normal' },
        { id: 's805', name: '陆小峰', gender: '男', studentNo: '20260207', phone: '138****1013', status: 'normal' },
        { id: 's806', name: '范小燕', gender: '女', studentNo: '20260208', phone: '139****1014', status: 'normal' },
        { id: 's807', name: '石小鹏', gender: '男', studentNo: '20260209', phone: '137****1015', status: 'normal' },
        { id: 's808', name: '廖小慧', gender: '女', studentNo: '20260210', phone: '136****1016', status: 'normal' },
        { id: 's809', name: '邹小涛', gender: '男', studentNo: '20260211', phone: '135****1017', status: 'normal' },
        { id: 's810', name: '熊小敏', gender: '女', studentNo: '20260212', phone: '134****1018', status: 'leave' },
        { id: 's811', name: '金小波', gender: '男', studentNo: '20260213', phone: '138****1019', status: 'normal' },
        { id: 's812', name: '魏小静', gender: '女', studentNo: '20260214', phone: '139****1020', status: 'normal' },
        { id: 's813', name: '陶小勇', gender: '男', studentNo: '20260215', phone: '137****1021', status: 'normal' },
        { id: 's814', name: '姜小琳', gender: '女', studentNo: '20260216', phone: '136****1022', status: 'normal' },
        { id: 's815', name: '董小强', gender: '男', studentNo: '20260217', phone: '135****1023', status: 'normal' }
      ],
      'class-2-5': [
        { id: 's901', name: '姚小刚', gender: '男', studentNo: '20260205', phone: '135****1011', status: 'leave' },
        { id: 's902', name: '谭小玲', gender: '女', studentNo: '20260206', phone: '134****1012', status: 'normal' },
        { id: 's903', name: '陆小峰', gender: '男', studentNo: '20260207', phone: '138****1013', status: 'normal' },
        { id: 's904', name: '范小燕', gender: '女', studentNo: '20260208', phone: '139****1014', status: 'normal' },
        { id: 's905', name: '石小鹏', gender: '男', studentNo: '20260209', phone: '137****1015', status: 'normal' },
        { id: 's906', name: '廖小慧', gender: '女', studentNo: '20260210', phone: '136****1016', status: 'normal' },
        { id: 's907', name: '邹小涛', gender: '男', studentNo: '20260211', phone: '135****1017', status: 'normal' },
        { id: 's908', name: '熊小敏', gender: '女', studentNo: '20260212', phone: '134****1018', status: 'leave' },
        { id: 's909', name: '金小波', gender: '男', studentNo: '20260213', phone: '138****1019', status: 'normal' },
        { id: 's910', name: '魏小静', gender: '女', studentNo: '20260214', phone: '139****1020', status: 'normal' },
        { id: 's911', name: '陶小勇', gender: '男', studentNo: '20260215', phone: '137****1021', status: 'normal' },
        { id: 's912', name: '姜小琳', gender: '女', studentNo: '20260216', phone: '136****1022', status: 'normal' },
        { id: 's913', name: '董小强', gender: '男', studentNo: '20260217', phone: '135****1023', status: 'normal' },
        { id: 's914', name: '黎小华', gender: '女', studentNo: '20260218', phone: '134****1024', status: 'normal' },
        { id: 's915', name: '贺小军', gender: '男', studentNo: '20260219', phone: '138****1025', status: 'leave' }
      ],
      'class-3-1': [
        { id: 'sa01', name: '陆小峰', gender: '男', studentNo: '20250101', phone: '138****2001', status: 'normal' },
        { id: 'sa02', name: '范小燕', gender: '女', studentNo: '20250102', phone: '139****2002', status: 'normal' },
        { id: 'sa03', name: '石小鹏', gender: '男', studentNo: '20250103', phone: '137****2003', status: 'normal' },
        { id: 'sa04', name: '廖小慧', gender: '女', studentNo: '20250104', phone: '136****2004', status: 'normal' },
        { id: 'sa05', name: '邹小涛', gender: '男', studentNo: '20250105', phone: '135****2005', status: 'leave' },
        { id: 'sa06', name: '熊小敏', gender: '女', studentNo: '20250106', phone: '134****2006', status: 'normal' },
        { id: 'sa07', name: '金小波', gender: '男', studentNo: '20250107', phone: '138****2007', status: 'normal' },
        { id: 'sa08', name: '魏小静', gender: '女', studentNo: '20250108', phone: '139****2008', status: 'normal' },
        { id: 'sa09', name: '陶小勇', gender: '男', studentNo: '20250109', phone: '137****2009', status: 'normal' },
        { id: 'sa10', name: '姜小琳', gender: '女', studentNo: '20250110', phone: '136****2010', status: 'normal' },
        { id: 'sa11', name: '董小强', gender: '男', studentNo: '20250111', phone: '135****2011', status: 'normal' },
        { id: 'sa12', name: '黎小华', gender: '女', studentNo: '20250112', phone: '134****2012', status: 'leave' },
        { id: 'sa13', name: '贺小军', gender: '男', studentNo: '20250113', phone: '138****2013', status: 'normal' },
        { id: 'sa14', name: '聂小芳', gender: '女', studentNo: '20250114', phone: '139****2014', status: 'normal' },
        { id: 'sa15', name: '薄小伟', gender: '男', studentNo: '20250115', phone: '137****2015', status: 'normal' }
      ],
      'class-3-2': [
        { id: 'sb01', name: '石小鹏', gender: '男', studentNo: '20250103', phone: '137****2003', status: 'normal' },
        { id: 'sb02', name: '廖小慧', gender: '女', studentNo: '20250104', phone: '136****2004', status: 'normal' },
        { id: 'sb03', name: '邹小涛', gender: '男', studentNo: '20250105', phone: '135****2005', status: 'leave' },
        { id: 'sb04', name: '熊小敏', gender: '女', studentNo: '20250106', phone: '134****2006', status: 'normal' },
        { id: 'sb05', name: '金小波', gender: '男', studentNo: '20250107', phone: '138****2007', status: 'normal' },
        { id: 'sb06', name: '魏小静', gender: '女', studentNo: '20250108', phone: '139****2008', status: 'normal' },
        { id: 'sb07', name: '陶小勇', gender: '男', studentNo: '20250109', phone: '137****2009', status: 'normal' },
        { id: 'sb08', name: '姜小琳', gender: '女', studentNo: '20250110', phone: '136****2010', status: 'normal' },
        { id: 'sb09', name: '董小强', gender: '男', studentNo: '20250111', phone: '135****2011', status: 'normal' },
        { id: 'sb10', name: '黎小华', gender: '女', studentNo: '20250112', phone: '134****2012', status: 'leave' },
        { id: 'sb11', name: '贺小军', gender: '男', studentNo: '20250113', phone: '138****2013', status: 'normal' },
        { id: 'sb12', name: '聂小芳', gender: '女', studentNo: '20250114', phone: '139****2014', status: 'normal' },
        { id: 'sb13', name: '薄小伟', gender: '男', studentNo: '20250115', phone: '137****2015', status: 'normal' },
        { id: 'sb14', name: '成小杰', gender: '女', studentNo: '20250116', phone: '136****2016', status: 'normal' },
        { id: 'sb15', name: '卫小刚', gender: '男', studentNo: '20250117', phone: '135****2017', status: 'normal' }
      ],
      'class-3-3': [
        { id: 'sc01', name: '邹小涛', gender: '男', studentNo: '20250105', phone: '135****2005', status: 'normal' },
        { id: 'sc02', name: '熊小敏', gender: '女', studentNo: '20250106', phone: '134****2006', status: 'leave' },
        { id: 'sc03', name: '金小波', gender: '男', studentNo: '20250107', phone: '138****2007', status: 'normal' },
        { id: 'sc04', name: '魏小静', gender: '女', studentNo: '20250108', phone: '139****2008', status: 'normal' },
        { id: 'sc05', name: '陶小勇', gender: '男', studentNo: '20250109', phone: '137****2009', status: 'normal' },
        { id: 'sc06', name: '姜小琳', gender: '女', studentNo: '20250110', phone: '136****2010', status: 'normal' },
        { id: 'sc07', name: '董小强', gender: '男', studentNo: '20250111', phone: '135****2011', status: 'normal' },
        { id: 'sc08', name: '黎小华', gender: '女', studentNo: '20250112', phone: '134****2012', status: 'leave' },
        { id: 'sc09', name: '贺小军', gender: '男', studentNo: '20250113', phone: '138****2013', status: 'normal' },
        { id: 'sc10', name: '聂小芳', gender: '女', studentNo: '20250114', phone: '139****2014', status: 'normal' },
        { id: 'sc11', name: '薄小伟', gender: '男', studentNo: '20250115', phone: '137****2015', status: 'normal' },
        { id: 'sc12', name: '成小杰', gender: '女', studentNo: '20250116', phone: '136****2016', status: 'normal' },
        { id: 'sc13', name: '卫小刚', gender: '男', studentNo: '20250117', phone: '135****2017', status: 'normal' },
        { id: 'sc14', name: '屈小萍', gender: '女', studentNo: '20250118', phone: '134****2018', status: 'normal' },
        { id: 'sc15', name: '项小磊', gender: '男', studentNo: '20250119', phone: '138****2019', status: 'leave' }
      ],
      'class-3-4': [
        { id: 'sd01', name: '金小波', gender: '男', studentNo: '20250107', phone: '138****2007', status: 'normal' },
        { id: 'sd02', name: '魏小静', gender: '女', studentNo: '20250108', phone: '139****2008', status: 'normal' },
        { id: 'sd03', name: '陶小勇', gender: '男', studentNo: '20250109', phone: '137****2009', status: 'normal' },
        { id: 'sd04', name: '姜小琳', gender: '女', studentNo: '20250110', phone: '136****2010', status: 'normal' },
        { id: 'sd05', name: '董小强', gender: '男', studentNo: '20250111', phone: '135****2011', status: 'normal' },
        { id: 'sd06', name: '黎小华', gender: '女', studentNo: '20250112', phone: '134****2012', status: 'leave' },
        { id: 'sd07', name: '贺小军', gender: '男', studentNo: '20250113', phone: '138****2013', status: 'normal' },
        { id: 'sd08', name: '聂小芳', gender: '女', studentNo: '20250114', phone: '139****2014', status: 'normal' },
        { id: 'sd09', name: '薄小伟', gender: '男', studentNo: '20250115', phone: '137****2015', status: 'normal' },
        { id: 'sd10', name: '成小杰', gender: '女', studentNo: '20250116', phone: '136****2016', status: 'normal' },
        { id: 'sd11', name: '卫小刚', gender: '男', studentNo: '20250117', phone: '135****2017', status: 'normal' },
        { id: 'sd12', name: '屈小萍', gender: '女', studentNo: '20250118', phone: '134****2018', status: 'normal' },
        { id: 'sd13', name: '项小磊', gender: '男', studentNo: '20250119', phone: '138****2019', status: 'leave' },
        { id: 'sd14', name: '舒小敏', gender: '女', studentNo: '20250120', phone: '139****2020', status: 'normal' },
        { id: 'sd15', name: '易小峰', gender: '男', studentNo: '20250121', phone: '137****2021', status: 'normal' }
      ],
      'class-3-5': [
        { id: 'se01', name: '陶小勇', gender: '男', studentNo: '20250109', phone: '137****2009', status: 'normal' },
        { id: 'se02', name: '姜小琳', gender: '女', studentNo: '20250110', phone: '136****2010', status: 'normal' },
        { id: 'se03', name: '董小强', gender: '男', studentNo: '20250111', phone: '135****2011', status: 'normal' },
        { id: 'se04', name: '黎小华', gender: '女', studentNo: '20250112', phone: '134****2012', status: 'leave' },
        { id: 'se05', name: '贺小军', gender: '男', studentNo: '20250113', phone: '138****2013', status: 'normal' },
        { id: 'se06', name: '聂小芳', gender: '女', studentNo: '20250114', phone: '139****2014', status: 'normal' },
        { id: 'se07', name: '薄小伟', gender: '男', studentNo: '20250115', phone: '137****2015', status: 'normal' },
        { id: 'se08', name: '成小杰', gender: '女', studentNo: '20250116', phone: '136****2016', status: 'normal' },
        { id: 'se09', name: '卫小刚', gender: '男', studentNo: '20250117', phone: '135****2017', status: 'normal' },
        { id: 'se10', name: '屈小萍', gender: '女', studentNo: '20250118', phone: '134****2018', status: 'normal' },
        { id: 'se11', name: '项小磊', gender: '男', studentNo: '20250119', phone: '138****2019', status: 'leave' },
        { id: 'se12', name: '舒小敏', gender: '女', studentNo: '20250120', phone: '139****2020', status: 'normal' },
        { id: 'se13', name: '易小峰', gender: '男', studentNo: '20250121', phone: '137****2021', status: 'normal' },
        { id: 'se14', name: '祝小丽', gender: '女', studentNo: '20250122', phone: '136****2022', status: 'normal' },
        { id: 'se15', name: '左小涛', gender: '男', studentNo: '20250123', phone: '135****2023', status: 'normal' }
      ]
    },
    // 寝室床位数据：roomId -> beds[]  每床 { bedNo, studentId, studentName, studentNo, className, checkedIn, status }
    // status: present=已到, not_bed=未到, holiday=节假日, leave=请假
    dormRooms: (() => {
      const names = ['张小明', '李小红', '王小华', '赵小丽', '陈小军', '刘小强', '孙小伟', '吴小峰', '郑小龙', '马小云', '周小杰', '黄小金', '许小梅', '韩小冬', '曹小芳', '曾小华', '彭小丽', '吕小强', '丁小伟', '任小杰', '沈小兰', '姚小刚', '谭小玲', '陆小峰', '范小燕', '石小鹏', '廖小慧', '邹小涛', '熊小敏', '金小波']
      const classes = ['高一(1)班', '高一(2)班', '高二(1)班', '高二(2)班', '高一(3)班', '高二(3)班']
      const statuses = ['present', 'not_bed', 'holiday', 'leave']
      const rooms = []
      const mkBeds = (base, statusMix) => {
        const beds = []
        for (let i = 1; i <= 6; i++) {
          const status = statusMix ? statuses[(base + i) % statuses.length] : 'not_bed'
          beds.push({
            bedNo: String(i),
            studentId: `s${base}${i}`,
            studentName: names[(base + i) % names.length],
            studentNo: `2026${String(1000 + base + i).slice(-4)}`,
            className: classes[(base + i) % classes.length],
            checkedIn: status === 'present',
            status
          })
        }
        return beds
      }
      // 混合状态寝室 - 展示红/绿/蓝/橙四种状态
      const roomConfigs = [
        ['男', 201, true], ['男', 202, true], ['男', 203, true], ['男', 204, true], ['男', 205, true],
        ['男', 206, true], ['男', 207, true], ['男', 208, true],
        ['女', 201, true], ['女', 202, true], ['女', 203, true], ['女', 204, true], ['女', 205, true], ['女', 206, true]
      ]
      roomConfigs.forEach(([building, n, mix], idx) => {
        const name = building + n
        rooms.push({
          id: 'room-' + name,
          name,
          building,
          beds: mkBeds((idx + 1) * 10, mix)
        })
      })
      return rooms
    })(),
    // 管理端：签到人脸流（打卡完成的学生，按打卡顺序）
    dormCheckInFaces: [
      { id: 's001', name: '张小明', className: '高一(1)班', room: '男301', time: '18:00' },
      { id: 's002', name: '李小红', className: '高一(1)班', room: '女201', time: '17:55' },
      { id: 's004', name: '赵小丽', className: '高一(1)班', room: '女201', time: '18:02' },
      { id: 's009', name: '吴小峰', className: '高二(1)班', room: '男302', time: '17:50' },
      { id: 's010', name: '郑小龙', className: '高二(1)班', room: '男302', time: '17:52' },
      { id: 's011', name: '马小云', className: '高二(2)班', room: '女301', time: '17:58' },
      { id: 's012', name: '黄小金', className: '高二(2)班', room: '女301', time: '18:01' },
      { id: 's013', name: '许小梅', className: '高一(1)班', room: '女201', time: '18:03' },
      { id: 's014', name: '韩小冬', className: '高一(1)班', room: '男302', time: '18:05' },
      { id: 's015', name: '曹小芳', className: '高一(1)班', room: '女201', time: '18:07' },
      { id: 's016', name: '曾小华', className: '高一(2)班', room: '男301', time: '18:08' },
      { id: 's017', name: '彭小丽', className: '高一(2)班', room: '女301', time: '18:10' },
      { id: 's018', name: '吕小强', className: '高一(2)班', room: '男301', time: '18:12' },
      { id: 's019', name: '丁小伟', className: '高一(2)班', room: '男302', time: '18:14' },
      { id: 's020', name: '任小杰', className: '高一(2)班', room: '男301', time: '18:15' },
      { id: 's021', name: '沈小兰', className: '高一(2)班', room: '女201', time: '18:16' },
      { id: 's022', name: '姚小刚', className: '高一(2)班', room: '男302', time: '18:18' },
      { id: 's023', name: '谭小玲', className: '高一(2)班', room: '女301', time: '18:20' },
      { id: 's024', name: '陆小峰', className: '高二(1)班', room: '男302', time: '18:22' },
      { id: 's025', name: '范小燕', className: '高二(1)班', room: '女301', time: '18:24' },
      { id: 's026', name: '石小鹏', className: '高二(1)班', room: '男302', time: '18:26' },
      { id: 's027', name: '廖小慧', className: '高一(1)班', room: '女201', time: '18:28' },
      { id: 's028', name: '邹小涛', className: '高一(2)班', room: '男301', time: '18:30' },
      { id: 's029', name: '熊小敏', className: '高二(2)班', room: '女301', time: '18:32' },
      { id: 's030', name: '金小波', className: '高二(2)班', room: '男302', time: '18:34' },
      { id: 's031', name: '钱小伟', className: '高一(1)班', room: '女201', time: '18:36' },
      { id: 's032', name: '冯小亮', className: '高一(2)班', room: '男301', time: '18:38' },
      { id: 's033', name: '陈小雪', className: '高一(1)班', room: '女201', time: '18:40' },
      { id: 's034', name: '张小燕', className: '高二(1)班', room: '男302', time: '18:42' },
      { id: 's035', name: '李明华', className: '高二(1)班', room: '女301', time: '18:44' },
      { id: 's036', name: '赵大伟', className: '高一(2)班', room: '男202', time: '18:46' },
      { id: 's037', name: '王小花', className: '高一(2)班', room: '女202', time: '18:48' },
      { id: 's038', name: '刘小梅', className: '高一(2)班', room: '女202', time: '18:50' },
      { id: 's039', name: '张小兰', className: '高二(1)班', room: '男303', time: '18:52' },
      { id: 's040', name: '赵小雨', className: '高二(2)班', room: '女302', time: '18:54' },
      { id: 's041', name: '钱小月', className: '高一(1)班', room: '男301', time: '18:56' },
      { id: 's042', name: '吴大峰', className: '高二(1)班', room: '男303', time: '18:58' },
      { id: 's043', name: '郑大龙', className: '高二(1)班', room: '男303', time: '19:00' },
      { id: 's044', name: '黄大金', className: '高三(1)班', room: '男401', time: '19:02' },
      { id: 's045', name: '马大云', className: '高三(1)班', room: '男401', time: '19:04' }
    ]
  },
  getters: {
    isAdmin: state => state.role === 'admin' || state.role === 'subadmin',
    isTeacher: state => state.role === 'teacher',
    isDorm: state => state.role === 'dorm',
    isMedical: state => state.role === 'medical',
    isStudentCouncil: state => state.role === 'studentCouncil',
    isGuard: state => state.role === 'guard',
    /** 教师端：优先显示 currentTeacher（来自 getTeacher）的真实姓名 */
    currentUserDisplayName: (state) => {
      const u = state.currentUser
      const psychologyCounselorName = getPsychologyCounselorName(state.userInfo)
      if (psychologyCounselorName) return psychologyCounselorName
      if (state.role === 'medical') {
        return getUserDisplayName(state.userInfo, 'medical') || u?.name || ''
      }
      if (state.role !== 'teacher') return u?.name || ''
      const t = state.currentTeacher
      if (t) return t.teacher_name ?? t.name ?? ''
      const teacherList = state.teachers?.list ?? []
      const userIds = new Set([
        state.userInfo?.user_id,
        state.userInfo?.id,
        state.userInfo?.teacher_id,
        state.currentUser?.id
      ].filter(value => value !== undefined && value !== null && value !== ''))
      const usernameCandidates = new Set([
        state.userInfo?.username,
        state.userInfo?.name,
        u?.name
      ].filter(Boolean).map(value => String(value).trim()))
      const matchedTeacher = teacherList.find(item => (
        userIds.has(item.userId)
        || userIds.has(item.teacherId)
        || usernameCandidates.has(String(item.contact ?? '').trim())
      ))
      if (matchedTeacher?.name) return matchedTeacher.name
      const realName = state.userInfo?.real_name || state.userInfo?.teacher_name || state.userInfo?.nickname
      if (realName) return realName
      return u?.name || ''
    },
    // 教师端：当前班级的学生
    myClassStudents: state => {
      if (state.role === 'teacher' && state.teacherClass) {
        return state.studentsByClass[state.teacherClass.id] || []
      }
      return []
    },
    // 教师端：当前班级的班牌（优先用 devices + classManage 真实数据）
    myBoard: (state) => {
      if (state.role !== 'teacher' || !state.teacherClass) return null
      const tc = state.teacherClass
      const devices = state.devices?.list ?? []
      const classes = state.classManage?.list ?? []
      if (devices.length && classes.length) {
        const cls = classes.find(c => String(c.id ?? c.class_id) === String(tc.id ?? tc.class_id))
          || classes.find(c => (c.name ?? c.class_name ?? '').trim() === (tc.name ?? tc.class_name ?? '').trim())
        const deviceId = cls?.deviceId ?? cls?.boardId ?? tc.boardId
        const dev = devices.find(d => String(d.deviceId ?? d.id) === String(deviceId))
        if (dev) {
          return {
            ...dev,
            id: dev.deviceId ?? dev.id,
            classId: cls?.id ?? '',
            className: cls?.name ?? tc.name ?? '',
            boardId: dev.deviceId ?? dev.id,
            machineCode: dev.machineCode ?? tc.machineCode ?? '',
            template: dev.template || 'classic'
          }
        }
      }
      return state.boards.find(b => b.classId === tc.id) || null
    },
    onlineBoardCount: state => state.boards.filter(b => b.status === 'online').length,
    // 教师端：本班学生所在寝室（含全部床位，便于查看同寝情况）
    myClassDormRooms: state => {
      if (state.role !== 'teacher' || !state.teacherClass) return []
      const classId = state.teacherClass.id
      const classStudents = state.studentsByClass[classId] || []
      const classIds = new Set(classStudents.map(s => s.id))
      return state.dormRooms.filter(room => room.beds.some(b => classIds.has(b.studentId)))
    },
    offlineBoardCount: state => state.boards.filter(b => b.status === 'offline').length,
    unboundBoardCount: state => state.boards.filter(b => b.status === 'unbound').length,
    unreadNotificationCount: state => state.notifications.filter(n => !n.read).length
  },
  mutations: {
    SET_AUTH_READY(state, ready) {
      state.authReady = !!ready
    },
    SET_ROLE(state, role) {
      state.role = role
      if (role === 'teacher') {
        state.currentUser = { id: 2, name: '张老师', avatar: '张', department: '高一年级组' }
      } else if (role === 'dorm') {
        state.currentUser = { id: 3, name: '王宿管', avatar: '王', department: '宿管部' }
      } else if (role === 'studentCouncil') {
        state.currentUser = { id: 4, name: '学生会', avatar: '学', department: '学生会' }
      } else if (role === 'guard') {
        state.currentUser = { id: 5, name: '门卫', avatar: '门', department: '门卫' }
      } else {
        state.currentUser = { id: 1, name: '李主任', avatar: '李', department: '教务处' }
      }
    },
    /** 设置登录态（用于 API 登录） */
    SET_AUTH(state, { token, userInfo, role, availableRoles = [] }) {
      state.token = token || ''
      state.userInfo = userInfo || null
      state.role = role || ''
      state.authRoles = Array.isArray(availableRoles) && availableRoles.length
        ? availableRoles.filter(isAllowedRole)
        : (role && isAllowedRole(role) ? [role] : [])
      state.isLoggedIn = !!token && isAllowedRole(role)
      if (userInfo) {
        const name = getUserDisplayName(userInfo, role)
        state.currentUser = {
          id: userInfo.user_id || userInfo.id,
          name,
          avatar: name.slice(0, 1),
          department: userInfo.role || userInfo.department || ''
        }
      } else {
        state.currentUser = { id: 1, name: '李主任', avatar: '李', department: '教务处' }
      }
    },
    LOGIN(state, role) {
      state.isLoggedIn = true
      state.role = role
      state.authRoles = role && isAllowedRole(role) ? [role] : []
      if (role === 'teacher') {
        state.currentUser = { id: 2, name: '张老师', avatar: '张', department: '高一年级组' }
      } else if (role === 'dorm') {
        state.currentUser = { id: 3, name: '王宿管', avatar: '王', department: '宿管部' }
      } else if (role === 'studentCouncil') {
        state.currentUser = { id: 4, name: '学生会', avatar: '学', department: '学生会' }
      } else if (role === 'guard') {
        state.currentUser = { id: 5, name: '门卫', avatar: '门', department: '门卫' }
      } else {
        state.currentUser = { id: 1, name: '李主任', avatar: '李', department: '教务处' }
      }
    },
    SET_SOCKET_CONNECTED(state, connected) {
      state.socketConnected = !!connected
    },
    /** 添加通知（不自动关闭，需用户点击已读） */
    ADD_NOTIFICATION(state, payload) {
      const n = {
        id: payload.id ?? `n_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        type: payload.type || 'info',
        title: payload.title || '通知',
        content: payload.content || '',
        read: false,
        timestamp: payload.timestamp ?? new Date().toISOString(),
        link: payload.link || null,
        payload: payload.payload || null
      }
      state.notifications.unshift(n)
      try {
        localStorage.setItem('notifications', JSON.stringify(state.notifications.slice(0, 100)))
      } catch (_) { void _ }
    },
    /** 标记单条通知已读 */
    MARK_NOTIFICATION_READ(state, id) {
      const n = state.notifications.find(x => x.id === id)
      if (n) n.read = true
      try {
        localStorage.setItem('notifications', JSON.stringify(state.notifications.slice(0, 100)))
      } catch (_) { void _ }
    },
    /** 全部已读 */
    MARK_ALL_NOTIFICATIONS_READ(state) {
      state.notifications.forEach(n => { n.read = true })
      try {
        localStorage.setItem('notifications', JSON.stringify(state.notifications.slice(0, 100)))
      } catch (_) { void _ }
    },
    /** 从本地恢复通知 */
    RESTORE_NOTIFICATIONS(state, list) {
      state.notifications = list || []
    },
    LOGOUT(state) {
      state.isLoggedIn = false
      state.socketConnected = false
      state.notifications = []
      state.token = ''
      state.userInfo = null
      state.role = ''
      state.authRoles = []
      state.serverNavigation = null
      state.currentUser = { id: 1, name: '李主任', avatar: '李', department: '教务处' }
      state.teacherClass = { id: 'class-1-1', name: '高一(1)班', grade: '高一', boardId: 'BP-2026-001', machineCode: 'MC-A3F7B2E1' }
      state.teacherClasses = [{ id: 'class-1-1', class_id: 'class-1-1', name: '高一(1)班', relationType: 1, isHeadTeacher: true }]
      state.currentTeacher = null
      state.teacherIsHeadTeacher = true
    },
    SET_SERVER_NAVIGATION(state, data) {
      if (data && Array.isArray(data.sections)) {
        state.serverNavigation = {
          role: data.role || '',
          portal: data.portal || '',
          isHeadTeacher: data.isHeadTeacher,
          sections: data.sections
        }
        return
      }
      state.serverNavigation = null
    },
    SET_ROLES_LIST(state, list) {
      state.rolesList = list || []
    },
    SET_CLASSES(state, list) {
      state.classes = list || []
    },
    ADD_STUDENT(state, { classId, student }) {
      if (!state.studentsByClass[classId]) state.studentsByClass[classId] = []
      state.studentsByClass[classId].push(student)
    },
    UPDATE_STUDENT(state, { classId, student }) {
      const list = state.studentsByClass[classId] || []
      const idx = list.findIndex(s => s.id === student.id)
      if (idx !== -1) list[idx] = { ...list[idx], ...student }
    },
    DELETE_STUDENT(state, { classId, studentId }) {
      const list = state.studentsByClass[classId] || []
      const idx = list.findIndex(s => s.id === studentId)
      if (idx !== -1) list.splice(idx, 1)
    },
    /** 同步班级学生列表（教师端 API 拉取后更新） */
    SET_STUDENTS_BY_CLASS(state, { classId, list }) {
      if (!classId) return
      state.studentsByClass = { ...state.studentsByClass, [classId]: list || [] }
    },
    /** 设置教师绑定的班级（根据 getTeacherClassRelations 解析） */
    SET_TEACHER_CLASS(state, cls) {
      if (cls) {
        const entry = buildTeacherClassEntry(cls, cls)
        const exists = state.teacherClasses.find(item => String(item.id ?? item.class_id) === String(entry.id ?? entry.class_id))
        state.teacherClasses = exists
          ? state.teacherClasses.map(item => String(item.id ?? item.class_id) === String(entry.id ?? entry.class_id) ? { ...item, ...entry } : item)
          : [...state.teacherClasses, entry]
        applyActiveTeacherClass(state, exists ? { ...exists, ...entry } : entry)
      }
    },
    SET_TEACHER_CLASSES(state, { classes = [], activeClassId = null } = {}) {
      const normalized = Array.isArray(classes)
        ? classes.map(item => cloneTeacherClassEntry(item)).filter(item => item.id || item.class_id)
        : []
      state.teacherClasses = normalized
      const preferred = activeClassId != null
        ? normalized.find(item => String(item.id ?? item.class_id) === String(activeClassId))
        : null
      if (preferred) {
        applyActiveTeacherClass(state, preferred)
        return
      }
      if (normalized.length) {
        applyActiveTeacherClass(state, normalized[0])
      }
    },
    SET_ACTIVE_TEACHER_CLASS(state, classId) {
      const matched = state.teacherClasses.find(item => String(item.id ?? item.class_id) === String(classId))
      if (matched) applyActiveTeacherClass(state, matched)
    },
    SET_CURRENT_TEACHER(state, teacher) {
      state.currentTeacher = teacher || null
    },
    /** 教师端：设置当前班级下是否为班主任（relation_type: 0=任课教师, 1=班主任） */
    SET_TEACHER_IS_HEAD(state, isHead) {
      state.teacherIsHeadTeacher = !!isHead
    }
  },
  actions: {
    async refreshServerNavigation({ commit }) {
      commit('SET_SERVER_NAVIGATION', null)
      return null
    },
    /** 钉钉免登 */
    async dingtalkLogin({ commit, dispatch }, payload) {
      const authCode = typeof payload === 'object' ? payload?.authCode : payload
      const targetPortal = typeof payload === 'object' ? payload?.targetPortal : ''
      const res = await apiDingtalkLogin(authCode)
      const auth = normalizeAuthResponse(res)
      if (!auth.success || !auth.token) {
        throw new Error(auth.message || '钉钉免登失败')
      }
      const info = auth.userInfo || {}
      const roleFromToken = getRoleFromToken(auth.token)
      const roleFromInfo = getRoleFromUserInfo(info)
      const { role, availableRoles } = resolveAuthRole({ token: auth.token, userInfo: info, targetPortal })
      const shouldValidateLegacyRole = false
      if (shouldValidateLegacyRole && roleFromToken && roleFromInfo && roleFromToken !== roleFromInfo) {
        throw new Error('钉钉免登失败：角色信息校验不一致')
      }
      const legacyResolvedRole = roleFromToken || roleFromInfo
      if (shouldValidateLegacyRole && !isAllowedRole(legacyResolvedRole)) {
        throw new Error('您的钉钉账号暂不支持登录该端，请使用管理端/教师端/宿管端账号')
      }
      localStorage.setItem('token', auth.token)
      localStorage.setItem('userInfo', JSON.stringify(info))
      localStorage.setItem('authRole', role)
      persistPortalSelection(normalizePortal(targetPortal) || resolvePortalFromRole(role))
      commit('SET_AUTH', { token: auth.token, userInfo: info, role, availableRoles })
      await dispatch('refreshServerNavigation').catch(() => null)
      if (role === 'teacher') {
        await dispatch('fetchTeacherClass').catch(() => {})
      }
      dispatch('initSocket')
      return { role, userInfo: info, availableRoles }
    },
    /** 用户登录 */
    async login({ commit, dispatch }, { username, password, targetPortal = '' }) {
      const res = await apiLogin(username, password)
      const auth = normalizeAuthResponse(res)
      if (!auth.success || !auth.token) {
        throw new Error(auth.message || '登录失败')
      }
      const info = auth.userInfo || {}
      const roleFromToken = getRoleFromToken(auth.token)
      const roleFromInfo = getRoleFromUserInfo(info)
      const { role, availableRoles } = resolveAuthRole({ token: auth.token, userInfo: info, targetPortal })
      const shouldValidateLegacyRole = false
      if (shouldValidateLegacyRole && roleFromToken && roleFromInfo && roleFromToken !== roleFromInfo) {
        throw new Error('登录失败：角色信息校验不一致')
      }
      const legacyResolvedRole = roleFromToken || roleFromInfo
      if (shouldValidateLegacyRole && !isAllowedRole(legacyResolvedRole)) {
        throw new Error('您的账号暂不支持登录该端')
      }
      localStorage.setItem('token', auth.token)
      localStorage.setItem('userInfo', JSON.stringify(info))
      localStorage.setItem('authRole', role)
      persistPortalSelection(normalizePortal(targetPortal) || resolvePortalFromRole(role))
      commit('SET_AUTH', { token: auth.token, userInfo: info, role, availableRoles })
      await dispatch('refreshServerNavigation').catch(() => null)
      if (role === 'teacher') {
        await dispatch('fetchTeacherClass').catch(() => {})
      }
      dispatch('initSocket')
      return { role, userInfo: info, availableRoles }
    },
    /** 登出：优先通知服务端失效 token，再清除本地状态 */
    async logout({ commit, state }) {
      disconnectSocket()
      if (state.token) {
        await apiLogout().catch(() => {})
      }
      clearAuthStorage()
      localStorage.removeItem('notifications')
      commit('LOGOUT')
    },
    /** 从 localStorage 恢复登录态（刷新页面时） */
    async initAuth({ state, commit, dispatch }) {
      if (state.authReady) return state.isLoggedIn
      if (authInitPromise) return authInitPromise

      commit('SET_AUTH_READY', false)

      authInitPromise = (async () => {
        const token = localStorage.getItem('token')
        if (!token) {
          commit('LOGOUT')
          return false
        }

        let userInfo = {}
        try {
          userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
        } catch (_) {
          clearAuthStorage()
          commit('LOGOUT')
          return false
        }

        let role = ''
        let availableRoles = []
        const savedRole = localStorage.getItem('authRole') || ''
        const savedPortal = localStorage.getItem(AUTH_PORTAL_STORAGE_KEY) || localStorage.getItem(LOGIN_PORTAL_STORAGE_KEY) || ''

        try {
          const roleFromToken = getRoleFromToken(token)
          const roleFromInfo = getRoleFromUserInfo(userInfo)
          const resolved = resolveAuthRole({ token, userInfo, targetPortal: savedPortal, savedRole })
          role = resolved.role
          availableRoles = resolved.availableRoles
          const shouldValidateLegacyRole = false
          if (shouldValidateLegacyRole && roleFromToken && roleFromInfo && roleFromToken !== roleFromInfo) {
            clearAuthStorage()
            commit('LOGOUT')
            return false
          }
          const legacyResolvedRole = roleFromToken || roleFromInfo
          if (shouldValidateLegacyRole && !isAllowedRole(legacyResolvedRole)) {
            clearAuthStorage()
            commit('LOGOUT')
            return false
          }
        } catch (_) {
          clearAuthStorage()
          commit('LOGOUT')
          return false
        }

        try {
          // 使用真实受鉴权的用户导航接口做登录态探活，避免公共接口误判登录有效
          await dispatch('refreshServerNavigation')
        } catch (_) {
          clearAuthStorage()
          commit('LOGOUT')
          return false
        }

        localStorage.setItem('authRole', role)
        persistPortalSelection(normalizePortal(savedPortal) || resolvePortalFromRole(role))
        commit('SET_AUTH', { token, userInfo, role, availableRoles })
        if (role === 'teacher') {
          await dispatch('fetchTeacherClass').catch(() => {})
        }
        dispatch('initSocket')
        return true
      })().finally(() => {
        commit('SET_AUTH_READY', true)
        authInitPromise = null
      })

      return authInitPromise
    },
    async switchAuthRole({ state, commit, dispatch }, target) {
      if (!state.token || !state.userInfo) {
        throw new Error('当前未登录，无法切换端口')
      }

      const availableRoles = Array.isArray(state.authRoles)
        ? state.authRoles.filter(isAllowedRole)
        : []
      if (!availableRoles.length) {
        throw new Error('当前账号没有可切换的端口权限')
      }

      const normalizedTarget = normalizePortal(target)
      const nextRole = availableRoles.includes(target)
        ? target
        : pickRoleByPortal(availableRoles, normalizedTarget)
      const nextPortal = normalizedTarget || resolvePortalFromRole(nextRole)

      if (!nextRole || !availableRoles.includes(nextRole)) {
        throw new Error(buildPortalPermissionError(normalizedTarget || target))
      }

      if (nextRole === state.role) {
        localStorage.setItem('authRole', nextRole)
        persistPortalSelection(nextPortal)
        return nextRole
      }

      localStorage.setItem('authRole', nextRole)
      persistPortalSelection(nextPortal)
      commit('SET_AUTH', {
        token: state.token,
        userInfo: state.userInfo,
        role: nextRole,
        availableRoles
      })
      await dispatch('refreshServerNavigation').catch(() => null)

      if (nextRole === 'teacher') {
        await dispatch('fetchTeacherClass').catch(() => {})
      }

      return nextRole
    },
    /** 初始化 WebSocket 连接（登录后自动调用） */
    initSocket({ state, commit }) {
      if (!state.token) return
      try {
        const saved = localStorage.getItem('notifications')
        if (saved) {
          const list = JSON.parse(saved)
          if (Array.isArray(list)) commit('RESTORE_NOTIFICATIONS', list)
        }
      } catch (_) { void _ }
      initSocket({
        token: state.token,
        onConnect: () => commit('SET_SOCKET_CONNECTED', true),
        onDisconnect: () => commit('SET_SOCKET_CONNECTED', false),
        onNewMessage: (msg) => {
          if (state.role === 'admin' || state.role === 'subadmin' || state.role === 'teacher') {
            commit('ADD_NOTIFICATION', {
              type: 'info',
              title: '新消息',
              content: `${msg.sender_name || '有人'}：${(msg.content || '').slice(0, 80)}${(msg.content || '').length > 80 ? '...' : ''}`,
              link: state.role === 'teacher' ? '/teacher/home-school' : '/home-school',
              payload: msg
            })
          }
        },
        onNewNotice: (notice) => {
          commit('ADD_NOTIFICATION', {
            type: 'info',
            title: '新通知',
            content: notice.title || '通知',
            link: '/notices',
            payload: notice
          })
        },
        onNewLeaveApplication: (data) => {
          if (state.role === 'teacher' || state.role === 'admin' || state.role === 'subadmin') {
            commit('ADD_NOTIFICATION', {
              type: 'warning',
              title: '新请假申请',
              content: `${data.student_name || '学生'} 提交了请假申请`,
              link: '/leave',
              payload: data
            })
          }
        },
        onApprovalResult: (data) => {
          const status = data.status === 1 || data.status === 'approved' ? '已通过' : '已驳回'
          commit('ADD_NOTIFICATION', {
            type: 'info',
            title: '请假审批结果',
            content: status,
            link: '/leave',
            payload: data
          })
        }
      })
    },
    /** 获取所有权限/角色列表 */
    async fetchRoles({ commit }) {
      const res = await getRoles()
      if (res.success && res.data) {
        commit('SET_ROLES_LIST', res.data)
        return res.data
      }
      return []
    },
    /** 教师端：getTeacher(user_id) 获取教师信息保存展示，getTeacherClassRelationsByTeacher(user_id) 获取 class_id 用于班级展示 */
    async fetchTeacherClass({ state, commit, dispatch }) {
      if (state.role !== 'teacher') return
      const initialUserId = state.userInfo?.user_id ?? state.userInfo?.id ?? state.userInfo?.teacher_id ?? state.currentUser?.id
      if (initialUserId == null) return
      try {
        const teacherRes = await getTeacher(initialUserId).catch(() => null)
        const teacher = teacherRes?.data ?? teacherRes
        if (teacher) commit('SET_CURRENT_TEACHER', teacher)

        await dispatch('teachers/fetchList').catch(() => {})
        const teacherList = state.teachers?.list ?? []
        const teacherNameCandidates = new Set([
          normalizeText(teacher?.teacher_name ?? teacher?.name),
          normalizeText(state.currentTeacher?.teacher_name ?? state.currentTeacher?.name),
          normalizeText(state.userInfo?.real_name),
          normalizeText(state.userInfo?.teacher_name),
          normalizeText(state.userInfo?.name),
          normalizeText(state.userInfo?.username),
          normalizeText(state.currentUser?.name)
        ].filter(Boolean))
        const teacherIdCandidates = new Set([
          state.userInfo?.user_id,
          state.userInfo?.id,
          state.userInfo?.teacher_id,
          state.currentUser?.id,
          teacher?.user_id,
          teacher?.userId,
          teacher?.teacher_id,
          teacher?.teacherId
        ].filter(value => value !== undefined && value !== null && value !== ''))

        const matchedTeacher = teacherList.find(item => (
          teacherIdCandidates.has(item.userId)
          || teacherIdCandidates.has(item.teacherId)
          || teacherNameCandidates.has(normalizeText(item.name))
        ))
        if (matchedTeacher) {
          commit('SET_CURRENT_TEACHER', matchedTeacher)
          teacherIdCandidates.add(matchedTeacher.userId)
          teacherIdCandidates.add(matchedTeacher.teacherId)
        }

        const relationIdCandidates = new Set(
          [...teacherIdCandidates]
            .filter(value => value !== undefined && value !== null && value !== '')
            .map(value => String(value))
        )

        const allRelationRes = await getTeacherClassRelations().catch(() => null)
        const allRelations = extractListFromResponse(allRelationRes)
        let relations = allRelations.filter(rel => relationIdCandidates.has(String(rel?.teacher_id ?? rel?.teacherId ?? '')))

        if (!relations.length && relationIdCandidates.size) {
          const relationResults = await Promise.all(
            [...relationIdCandidates].map(id => getTeacherClassRelationsByTeacher(id).catch(() => null))
          )
          relations = relationResults.flatMap(item => extractListFromResponse(item))
        }

        const relationMap = new Map()
        relations
          .filter(rel => rel?.class_id != null)
          .forEach(rel => {
            const key = `${rel.class_id}_${rel.relation_type ?? rel.relationType ?? 0}`
            if (!relationMap.has(key)) relationMap.set(key, rel)
          })
        relations = Array.from(relationMap.values())

        await dispatch('classManage/fetchList', { page_size: 200 }).catch(() => {})
        const list = state.classManage?.list ?? []
        const classMap = new Map()
        list.forEach(item => {
          const key = String(item.id ?? item.class_id)
          if (key) classMap.set(key, item)
        })
        const mergedMap = new Map()
        relations
          .filter(rel => rel?.class_id != null)
          .forEach(rel => {
            const key = String(rel.class_id)
            const cls = classMap.get(key) || {}
            const next = buildTeacherClassEntry(cls, rel)
            const prev = mergedMap.get(key)
            if (!prev || (prev.relationType ?? 0) !== 1 && next.relationType === 1) {
              mergedMap.set(key, next)
            }
          })
        if (!mergedMap.size && state.teacherClass?.id) {
          mergedMap.set(String(state.teacherClass.id), buildTeacherClassEntry(state.teacherClass, state.teacherClass))
        }

        const ordered = Array.from(mergedMap.values()).sort((a, b) => {
          if (a.isHeadTeacher !== b.isHeadTeacher) return a.isHeadTeacher ? -1 : 1
          return String(a.name || '').localeCompare(String(b.name || ''), 'zh-Hans-CN')
        })
        if (!ordered.length) return

        const savedClassId = localStorage.getItem(TEACHER_ACTIVE_CLASS_STORAGE_KEY)
        const activeClassId = savedClassId && ordered.some(item => String(item.id ?? item.class_id) === String(savedClassId))
          ? savedClassId
          : (ordered.find(item => item.isHeadTeacher)?.id ?? ordered[0].id)
        commit('SET_TEACHER_CLASSES', { classes: ordered, activeClassId })
        localStorage.setItem(TEACHER_ACTIVE_CLASS_STORAGE_KEY, String(activeClassId))
      } catch (_) {
        return
      }
    },
    selectTeacherClass({ state, commit }, classId) {
      if (classId == null) return
      const matched = state.teacherClasses.find(item => String(item.id ?? item.class_id) === String(classId))
      if (!matched) return
      commit('SET_ACTIVE_TEACHER_CLASS', classId)
      localStorage.setItem(TEACHER_ACTIVE_CLASS_STORAGE_KEY, String(matched.id ?? matched.class_id))
    }
  },
  modules: {
    evaluationProject,
    evaluationType,
    classManage: classes,
    classHonors,
    notices,
    teachers,
    students,
    devices: devicesModule,
    attendance,
    attendanceFaceEnrollment
  }
})
